import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';

import { Card } from '@repo/components/card';

import { formatDate } from '@repo/utils/format-date';

import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { NotFoundError } from '~/services/http.ts';
import { getInvoiceById, queryInvoiceById } from '~/services/invoice/invoice.ts';

import { InvoiceLinesTable } from './-components/invoice-lines-table.tsx';
import { invoicePeriodLabel, invoiceTypeLabel } from './-utils.ts';

export const Route = createFileRoute('/project/$project/(invoices)/invoices/$invoice')({
  component: RouteInvoiceById,
  loader: async ({ params: { project, invoice } }) => {
    try {
      return await getInvoiceById(project, invoice);
    }
    catch (e) {
      if (e instanceof NotFoundError)
        throw notFound();
      throw e;
    }
  },
  notFoundComponent: () => (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <h1 className="text-2xl font-semibold text-secondary-800">Invoice not found</h1>
      <p className="text-secondary-500">There is no invoice with this reference.</p>
    </div>
  ),
});

function RouteInvoiceById() {
  const { project: projectId, invoice: invoiceId } = Route.useParams();
  const invoice = useSuspenseQuery(queryInvoiceById(projectId, invoiceId)).data;
  const facts: [string, string][] = [
    ['Period', invoicePeriodLabel(invoice.periodStart, invoice.periodEnd)],
    ...(invoice.cargo ? [['Cargo', invoice.cargo] as [string, string]] : []),
    ...(invoice.approvedOn ? [['Approved', formatDate(invoice.approvedOn, 'd MMM yyyy')] as [string, string]] : []),
  ];

  return (
    <div className="@container flex flex-col items-start gap-6 pb-6 max-w-7xl mx-auto w-full">
      <div className="flex flex-col gap-4 w-full @lg:flex-row @lg:justify-between pb-4 border-b border-b-secondary-200">
        <ContentHeader back={{ to: '..' }}>{invoiceTypeLabel(invoice.type)}</ContentHeader>
        <dl className="flex flex-wrap gap-6 items-center">
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt className="text-secondary-500 text-sm">{label}</dt>
              <dd className="font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <Card className="p-0 gap-0 w-full overflow-hidden">
        <div className="border-b border-secondary-200 bg-secondary-100/50 px-6 py-3">
          <h2 className="text-sm font-semibold text-secondary-600">{`Lines (${String(invoice.lines.length)})`}</h2>
        </div>
        <div className="overflow-x-auto">
          {invoice.lines.length > 0
            ? <InvoiceLinesTable lines={invoice.lines} total={invoice.total} currency={invoice.currency} />
            : <div className="px-6 py-8 text-center text-secondary-500">This invoice has no lines.</div>}
        </div>
      </Card>
    </div>
  );
}
